import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setActionType,
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import AppointmentOutcomeDialog from "components/appointment-outcome-dialog";
import { asksCancellationMessage } from "components/cancellation-message-choice";
import {
  APPOINTMENT_STATUS_CANCELED,
  APPOINTMENT_STATUS_COMPLETED
} from "constants/appointments";
import { CHART_NOTE_COMPLETED } from "constants/chart-notes";
import { NEW_APPOINTMENT_OUTCOME_CHANGES } from "constants/feature-flags";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { useUpdateAppointmentMutation } from "services/appointments";
import { useUpdateChartNoteMutation } from "services/chart-notes";
import { eventsApi } from "services/events";
import { Diagnosis } from "services/types/diagnosis.type";

import { AppointmentOutcomeForm } from "../components/appointment-outcome-dialog/appointment-outcome-dialog.component";
import ErrorSnackbarContent from "../components/error-snackbar-content";

type SelectAppointmentOutcomeProviderProps = {
  children?: React.ReactNode;
};

const SelectAppointmentOutcomeContext = React.createContext<any>(null);

export function SelectAppointmentOutcomeProvider({
  children
}: SelectAppointmentOutcomeProviderProps) {
  const flags = useFlags();
  const newAppointmentOutcomeChangesEnabled =
    flags[NEW_APPOINTMENT_OUTCOME_CHANGES];
  const dispatch = useAppDispatch();
  const [appointmentOutcomeDialogOpen, setAppointmentOutcomeDialogOpen] =
    React.useState(false);
  const [currentAppointmentId, setCurrentAppointmentId] = useState<
    number | null
  >(null);
  const [postUpdateAction, setPostUpdateAction] = useState<() => void>(
    () => {}
  );
  const [onCancelAction, setOnCancelAction] = useState<() => void>(() => {});
  // Optional, and only Scheduling V3's Room Traffic passes them (ENG-2214): a step that
  // must succeed before anything is written, or nothing is; and its undo, run when a
  // write after it fails.
  const [beforeUpdateAction, setBeforeUpdateAction] = useState<
    (() => Promise<boolean>) | undefined
  >(undefined);
  const [updateFailedAction, setUpdateFailedAction] = useState<
    (() => Promise<void>) | undefined
  >(undefined);
  // The whole submit, not just the appointment PATCH: the chart note and the step above
  // run first, and a Confirm or Esc during them must not start a second submit or close
  // a dialog whose write is still going to land. The ref answers a second click in the
  // same tick, before the state has re-rendered the button.
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appointmentStatus, setAppointmentStatus] = useState<number>(
    APPOINTMENT_STATUS_COMPLETED
  );
  // "Send cancellation message?" answered before the dialog opened (the old scheduler's
  // cancel confirmation asks it first). Undefined means this dialog asks it itself.
  const [presetCancellationAnswer, setPresetCancellationAnswer] = useState<
    boolean | undefined
  >(undefined);
  const practice = useAppSelector(selectCurrentPractice);
  const askCancellationMessage =
    appointmentStatus === APPOINTMENT_STATUS_CANCELED &&
    presetCancellationAnswer === undefined &&
    asksCancellationMessage(practice);

  const [updateAppointment, { isLoading: isUpdatingOutcome }] =
    useUpdateAppointmentMutation();
  const [updateNote] = useUpdateChartNoteMutation();

  const initAppointmentOutcomeDialog = useCallback(
    (
      appointmentId: number,
      postUpdate: () => void,
      onCancel: () => void,
      status: number = APPOINTMENT_STATUS_COMPLETED,
      beforeUpdate?: () => Promise<boolean>,
      onUpdateFailed?: () => Promise<void>,
      options?: { sendCancellationMessage?: boolean }
    ) => {
      setCurrentAppointmentId(appointmentId);
      setAppointmentOutcomeDialogOpen(true);
      setPostUpdateAction(() => postUpdate);
      setOnCancelAction(() => onCancel);
      setBeforeUpdateAction(() => beforeUpdate);
      setUpdateFailedAction(() => onUpdateFailed);
      setAppointmentStatus(status);
      setPresetCancellationAnswer(options?.sendCancellationMessage);
    },
    []
  );

  const writeOutcome = useCallback(
    async ({
      chart_note_id,
      outcome_reason,
      outcome,
      outcome_notes,
      hearing_test_conducted,
      diagnosis,
      telehealth_provider,
      companion_present,
      hearing_aid_demo_performed,
      companion_type,
      companion_name,
      send_cancellation_message
    }: Partial<AppointmentOutcomeForm> & {
      /** The dialog's own answer to "Send cancellation message?", when it asked. */
      send_cancellation_message?: boolean;
    }) => {
      const sendCancellationMessage =
        appointmentStatus === APPOINTMENT_STATUS_CANCELED
          ? (send_cancellation_message ?? presetCancellationAnswer)
          : undefined;
      const appointmentWithOutcome = {
        id: Number(currentAppointmentId),
        outcome: Number(outcome) || undefined,
        outcome_reason: Number(outcome_reason)
          ? { id: Number(outcome_reason) }
          : undefined,
        outcome_notes: newAppointmentOutcomeChangesEnabled
          ? undefined
          : outcome_notes,
        status: appointmentStatus,
        hearing_test_conducted: Boolean(hearing_test_conducted),
        diagnosis: diagnosis as Partial<Diagnosis>,
        telehealth_provider,
        companion_present,
        hearing_aid_demo_performed,
        companionType: companion_present ? companion_type || null : null,
        companionName: companion_present ? companion_name || "" : "",
        send_cancellation_message: sendCancellationMessage
      };
      // First, before the chart note is marked completed: a refused step must leave
      // nothing written. It reports its own failure; the dialog stays open to try again.
      if (beforeUpdateAction && !(await beforeUpdateAction())) return false;
      const failed = async () => {
        if (updateFailedAction) await updateFailedAction();
        return false;
      };
      if (newAppointmentOutcomeChangesEnabled && chart_note_id) {
        const appointmentNote = {
          id: chart_note_id,
          text: outcome_notes || undefined,
          icd10_codes: (diagnosis?.icd10_codes || []) as unknown as number[],
          status: CHART_NOTE_COMPLETED
        };
        try {
          const resp = await updateNote(appointmentNote);
          if ("error" in resp) {
            dispatch(
              setCustomMessage(<ErrorSnackbarContent response={resp} />)
            );
            dispatch(setOpen(true));
            return failed();
          }
        } catch (err) {
          dispatch(setCustomMessage(<ErrorSnackbarContent response={err} />));
          dispatch(setOpen(true));
          return failed();
        }
      }
      try {
        await updateAppointment(appointmentWithOutcome).unwrap();
        dispatch(eventsApi.util.invalidateTags(["Events"]));
        if (postUpdateAction) {
          postUpdateAction();
        } else {
          dispatch(setActionType("update"));
          dispatch(setOpen(true));
        }
        setCurrentAppointmentId(null);
        setAppointmentOutcomeDialogOpen(false);
        // Errors are handled (snackbar) inside this hook, so callers can't use
        // rejection to detect failure — follow-up side effects (e.g. creating a
        // call back entry) must check this return value instead.
        return true;
      } catch (err) {
        dispatch(setCustomMessage(<ErrorSnackbarContent response={err} />));
        dispatch(setOpen(true));
        return failed();
      }
    },
    [
      updateAppointment,
      currentAppointmentId,
      dispatch,
      postUpdateAction,
      beforeUpdateAction,
      updateFailedAction,
      newAppointmentOutcomeChangesEnabled,
      updateNote,
      appointmentStatus,
      presetCancellationAnswer
    ]
  );

  const submitOutcomeReason = useCallback(
    async (
      form: Partial<AppointmentOutcomeForm> & {
        send_cancellation_message?: boolean;
      }
    ) => {
      if (submitting.current) return false;
      submitting.current = true;
      setIsSubmitting(true);
      try {
        return await writeOutcome(form);
      } finally {
        submitting.current = false;
        setIsSubmitting(false);
      }
    },
    [writeOutcome]
  );

  return (
    <SelectAppointmentOutcomeContext.Provider
      value={{
        appointmentOutcomeDialogOpen,
        setAppointmentOutcomeDialogOpen,
        initAppointmentOutcomeDialog,
        currentAppointmentId,
        setCurrentAppointmentId,
        submitOutcomeReason,
        isUpdatingOutcome: isUpdatingOutcome || isSubmitting,
        onCancelAction,
        appointmentStatus,
        askCancellationMessage
      }}
    >
      {children}

      {appointmentOutcomeDialogOpen && <AppointmentOutcomeDialog />}
    </SelectAppointmentOutcomeContext.Provider>
  );
}

export default function useAppointmentOutcome() {
  const appointmentOutcomeContext = React.useContext(
    SelectAppointmentOutcomeContext
  );
  if (!appointmentOutcomeContext) {
    throw new Error(
      `useAppointmentOutcome must be used within a SelectAppointmentOutcomeProvider`
    );
  }

  return appointmentOutcomeContext;
}
